import { TILE, COLS, ROWS, W, H, MAX_HP, MAIN_LEVELS, effLevel, LEVELS, WEAPONS, WEAPON_ORDER, WEAPON_UNLOCK, DIFFICULTIES, ENEMIES, bossId } from "./config";
import { kitFor, bossPower, moveLabel, moveCooldown, weaponAngle, isWindup } from "./bossMoves";
import { fx, updateFx, drawFx, drawVignette, drawFade } from "./effects";
import { hazardFor, pickHazard, hazardInterval, drawHazard, hazardColor } from "./hazards";
import { SPR, drawSprite, drawEnemy, drawBoss, drawHero, drawWeapon } from "./spritesClassic";
import { sfx, playMusic } from "./audio";
import { biomeFor, drawDecor } from "./biomes";
import { drawBossExtras } from "./sprites";

const rnd = (a, b) => a + Math.random() * (b - a);
const ri = (a, b) => Math.floor(rnd(a, b + 1));
const angDiff = (a, b) => {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return Math.abs(d);
};
const DIRS = [[1, 0], [-1, 0], [0, 1], [0, -1]];
const CLASSIC_BOSSES = {
  3: { name: "Crumb King", type: "crumb", scale: 3, hp: 40, speed: 50, ringK: 0.5 },
  6: { name: "Jam Kraken", type: "jam", scale: 3, hp: 60, speed: 55, ringK: 0.7 },
  9: { name: "Mold Titan", type: "mold", scale: 2.5, hp: 85, speed: 40, ringK: 0.85 },
  12: { name: "Burnt Baron", type: "ghost", scale: 3, hp: 70, speed: 50, ringK: 0.8 },
  15: { name: "Toaster Overlord", hp: 95, speed: 40, ringK: 0.85 },
};
export const POWERS = ["shield", "speed", "double", "magnet", "star"];
export const POWER_INFO = {
  shield: { label: "Shield", icon: "S", color: "#67E8F9", dur: 0 },
  speed: { label: "Speed Boots", icon: ">>", color: "#4ADE80", dur: 10 },
  double: { label: "Double Toast", icon: "x2", color: "#F59E0B", dur: 12 },
  magnet: { label: "Toast Magnet", icon: "U", color: "#F87171", dur: 12 },
  star: { label: "Invincible", icon: "*", color: "#FDE047", dur: 6 },
};
const KEYMAP = { w: "up", arrowup: "up", s: "down", arrowdown: "down", a: "left", arrowleft: "left", d: "right", arrowright: "right", " ": "atk", j: "atk", k: "atk" };

export class Game {
  constructor(canvas, difficulty, cb, opts = {}) {
    this.c = canvas;
    this.ctx = canvas.getContext("2d");
    this.cb = cb;
    this.diffKey = difficulty;
    this.d = DIFFICULTIES[difficulty];
    this.maxHp = MAX_HP + (opts.extraHearts || 0) * 2;
    this.bonusPack = !!opts.bonusPack;
    this.skin = opts.skin || null;
    this.run = { level: opts.startLevel || 1, lives: 3 + (opts.extraLives || 0), weapon: opts.weapon || "butter_knife", toastTotal: 0, kills: 0, score: 0, livesLost: 0, smashes: 0, stomps: 0, bees: 0, start: performance.now() };
    this.input = { up: false, down: false, left: false, right: false, atk: false };
    this.fired = new Set();
    this.paused = false;
    this.t = 0;
    this.hudKey = "";
    this.bind();
    this.startLevel();
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.loop);
  }

  bind() {
    this.onKey = (down) => (e) => {
      const k = KEYMAP[e.key.toLowerCase()];
      if (k) { this.input[k] = down; e.preventDefault(); }
      if (down && (e.key === "Escape" || e.key.toLowerCase() === "p")) this.cb.onPauseToggle?.();
    };
    this.kd = this.onKey(true);
    this.ku = this.onKey(false);
    this.pd = (e) => {
      if (e.pointerType !== "mouse") return;
      const r = this.c.getBoundingClientRect();
      const mx = ((e.clientX - r.left) / r.width) * W;
      const my = ((e.clientY - r.top) / r.height) * H;
      if (this.phase === "play" && !this.paused) this.tryAttack(Math.atan2(my - this.p.y, mx - this.p.x));
    };
    window.addEventListener("keydown", this.kd);
    window.addEventListener("keyup", this.ku);
    this.c.addEventListener("pointerdown", this.pd);
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    window.removeEventListener("keydown", this.kd);
    window.removeEventListener("keyup", this.ku);
    this.c.removeEventListener("pointerdown", this.pd);
  }

  achieve(id) {
    if (this.fired.has(id)) return;
    this.fired.add(id);
    this.cb.onAchieve?.(id);
  }

  addScore(n) { this.run.score += Math.round(n * this.d.scoreMult); }

  loop = (now) => {
    const dt = Math.min(0.033, (now - this.last) / 1000);
    this.last = now;
    if (!this.paused) this.update(dt);
    this.draw();
    this.emitHud();
    this.raf = requestAnimationFrame(this.loop);
  };

  emitHud() {
    const b = this.boss;
    const pw = this.pw;
    const powers = POWERS.filter((k) => (k === "shield" ? pw.shield : pw[k] > 0)).map((k) => (k === "shield" ? k : `${k}:${Math.ceil(pw[k])}`));
    const hud = { powers, maxHp: this.maxHp, hp: Math.max(0, this.p.hp), lives: this.run.lives, toast: this.levelToast, goal: this.goal, level: this.run.level, weapon: this.run.weapon, score: this.run.score, boss: b && !this.bossDead ? Math.ceil((b.hp / b.maxHp) * 100) : null, exitOpen: !!this.exit, bossName: b?.name, timeLeft: Math.max(0, Math.ceil(this.timeLimit - this.levelTime)) };
    const key = JSON.stringify(hud);
    if (key !== this.hudKey) { this.hudKey = key; this.cb.onHud?.(hud); }
  }

  stats() {
    return { score: this.run.score, level: this.run.level, toast: this.run.toastTotal, kills: this.run.kills, livesLost: this.run.livesLost, difficulty: this.diffKey, time: Math.round((performance.now() - this.run.start) / 1000) };
  }

  // ---------- level setup ----------
  startLevel() {
    const L = this.run.level;
    const bio = biomeFor(L);
    this.theme = { ...LEVELS[L - 1], floor: bio.midDark, wall: bio.crustDark, kind: bio.kind, wallHi: bio.crust };
    this.hazard = hazardFor(bio.kind);
    this.hazards = []; this.hazardT = 6; this.hazardsSpawned = 0; this.hazardHit = false; this.bossHurt = false;
    this.goal = Math.max(4, Math.round(this.theme.goal * 1.35 * this.d.goal));
    this.levelToast = 0;
    this.levelDamage = false;
    this.levelTime = 0; this.timedOut = false;
    this.timeLimit = this.diffKey === "hard" ? 135 : 90;
    this.genMap(L);
    this.pw = this.bonusPack ? { shield: true, speed: 0, double: 25, magnet: 25, star: 6 } : { shield: false, speed: 0, double: 0, magnet: 0, star: 0 };
    this.powerT = 35;
    this.p = { x: 15 * TILE + 16, y: 10 * TILE + 16, r: 11, hp: this.maxHp, inv: 2, face: 0, atk: 0, swing: 0, swingAng: 0, kb: { x: 0, y: 0, t: 0 }, walk: 0 };
    this.enemies = []; this.bullets = []; this.toasts = []; this.pickups = []; this.parts = []; this.texts = []; this.fx = [];
    this.exit = null; this.boss = null; this.bossDead = !CLASSIC_BOSSES[L];
    this.spawnT = 2; this.toastT = 0; this.shake = 0;
    this.maxEnemies = Math.max(1, Math.round((2 + effLevel(L) * 1.6) * this.d.enemyCount)) + (L === MAIN_LEVELS ? Math.round(5 * this.d.enemyCount) : 0);
    this.spawnInt = Math.max(0.45, 3.2 - L * 0.29) / this.d.spawnRate;
    this.computeFlow();
    for (let i = 0; i < Math.ceil(this.maxEnemies / 2); i++) this.spawnEnemy();
    for (let i = 0; i < 4; i++) this.spawnToast();
    const wu = Object.entries(WEAPON_UNLOCK).filter(([lv]) => +lv <= L).map(([, w]) => w).pop();
    if (wu && WEAPON_ORDER.indexOf(wu) > WEAPON_ORDER.indexOf(this.run.weapon)) {
      const [x, y] = this.randPos(6);
      this.pickups.push({ type: "weapon", weapon: wu, x, y });
    }
    const cb = CLASSIC_BOSSES[L];
    if (cb) {
      const pw = bossPower(this.diffKey, !cb.type);
      const hp = Math.round(cb.hp * this.d.enemyHp * pw.hp);
      this.boss = { ...cb, kit: kitFor(cb.name), pw, move: null, moveT: 3.5, moveIdx: 0, face: Math.PI / 2, x: 15 * TILE + 16, y: 4 * TILE, r: cb.type ? ENEMIES[cb.type].r * cb.scale : 30, hp, maxHp: hp, speed: cb.speed * this.d.enemySpeed, ringT: 3, aimT: 2, hitT: 0, warn: 0, enraged: false, isBoss: true };
    }
    if (L >= 5) this.achieve("level_5");
    if (L >= 8) this.achieve("globetrotter");
    if (this.skin) this.achieve("dressed_up");
    if (L >= MAIN_LEVELS) this.achieve("final_level");
    this.phase = "intro";
    this.phaseT = 2.4;
    sfx(cb ? "boss" : "level");
    playMusic(cb ? "boss" : "level", L);
  }

  genMap(L) {
    const g = [];
    for (let y = 0; y < ROWS; y++) {
      g.push([]);
      for (let x = 0; x < COLS; x++) g[y].push(x === 0 || y === 0 || x === COLS - 1 || y === ROWS - 1 ? 1 : 0);
    }
    const blocks = Math.min(26, 10 + L * 1.5);
    for (let i = 0; i < blocks; i++) {
      const w = ri(1, 4), h = ri(1, 3);
      const x = ri(2, COLS - 3 - w), y = ri(2, ROWS - 3 - h);
      if (x + w > 12 && x < 19 && y + h > 7 && y < 13) continue;
      if (CLASSIC_BOSSES[L] && x + w > 10 && x < 21 && y < 7) continue;
      for (let yy = y; yy < y + h; yy++) for (let xx = x; xx < x + w; xx++) g[yy][xx] = 1;
    }
    const seen = new Set([`15,10`]);
    const q = [[15, 10]];
    while (q.length) {
      const [x, y] = q.shift();
      for (const [dx, dy] of DIRS) {
        const nx = x + dx, ny = y + dy, k = `${nx},${ny}`;
        if (g[ny][nx] === 0 && !seen.has(k)) { seen.add(k); q.push([nx, ny]); }
      }
    }
    this.floor = [];
    for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
      if (g[y][x] === 0 && !seen.has(`${x},${y}`)) g[y][x] = 1;
      if (g[y][x] === 0) this.floor.push([x, y]);
    }
    this.grid = g;
    this.renderBg();
  }

  renderBg() {
    const bg = document.createElement("canvas");
    bg.width = W; bg.height = H;
    const c = bg.getContext("2d");
    c.fillStyle = this.theme.floor;
    c.fillRect(0, 0, W, H);
    for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
      const px = x * TILE, py = y * TILE;
      if (this.grid[y][x]) {
        c.fillStyle = this.theme.wall; c.fillRect(px, py, TILE, TILE);
        c.fillStyle = this.theme.wallHi; c.fillRect(px, py, TILE, 5);
        c.fillStyle = "rgba(255,255,255,0.18)"; c.fillRect(px, py, TILE, 2);
        c.fillStyle = "rgba(0,0,0,0.35)"; c.fillRect(px, py + TILE - 6, TILE, 6);
        c.fillStyle = "rgba(0,0,0,0.15)"; c.fillRect(px + 15, py + 4, 2, 10); c.fillRect(px + 6, py + 16, 2, 10);
      } else {
        if ((x + y) % 2) { c.fillStyle = "rgba(255,255,255,0.03)"; c.fillRect(px, py, TILE, TILE); }
        if (Math.random() < 0.08) { c.fillStyle = "rgba(255,255,255,0.08)"; c.fillRect(px + ri(4, 26), py + ri(4, 26), 3, 3); }
        if (this.grid[y - 1]?.[x]) { c.fillStyle = "rgba(0,0,0,0.25)"; c.fillRect(px, py, TILE, 6); }
        else if (Math.random() < 0.06) drawDecor(c, this.theme.kind, px + 16, py + 26, Math.random(), 0.75);
      }
    }
    this.bg = bg;
  }

  solid(tx, ty) { return tx < 0 || ty < 0 || tx >= COLS || ty >= ROWS || this.grid[ty][tx] === 1; }

  collides(x, y, r) {
    const x0 = Math.floor((x - r) / TILE), x1 = Math.floor((x + r) / TILE);
    const y0 = Math.floor((y - r) / TILE), y1 = Math.floor((y + r) / TILE);
    for (let ty = y0; ty <= y1; ty++) for (let tx = x0; tx <= x1; tx++) if (this.solid(tx, ty)) return true;
    return false;
  }

  move(e, dx, dy) {
    if (!this.collides(e.x + dx, e.y, e.r)) e.x += dx;
    if (!this.collides(e.x, e.y + dy, e.r)) e.y += dy;
  }

  randPos(minDist) {
    const px = Math.floor((this.p?.x ?? 496) / TILE), py = Math.floor((this.p?.y ?? 336) / TILE);
    const opts = this.floor.filter(([x, y]) => Math.abs(x - px) + Math.abs(y - py) >= minDist);
    const [x, y] = (opts.length ? opts : this.floor)[ri(0, (opts.length ? opts : this.floor).length - 1)];
    return [x * TILE + 16, y * TILE + 16];
  }

  computeFlow() {
    const f = this.grid.map((row) => row.map(() => -1));
    const sx = Math.floor(this.p.x / TILE), sy = Math.floor(this.p.y / TILE);
    if (this.solid(sx, sy)) { this.flow = f; return; }
    f[sy][sx] = 0;
    const q = [[sx, sy]];
    for (let i = 0; i < q.length; i++) {
      const [x, y] = q[i];
      for (const [dx, dy] of DIRS) {
        const nx = x + dx, ny = y + dy;
        if (!this.solid(nx, ny) && f[ny][nx] < 0) { f[ny][nx] = f[y][x] + 1; q.push([nx, ny]); }
      }
    }
    this.flow = f;
  }

  spawnEnemy() {
    const L = this.run.level;
    const pool = Object.entries(ENEMIES).filter(([, e]) => e.minLevel <= L && !e.sideOnly);
    let roll = Math.random() * pool.reduce((s, [, e]) => s + e.weight, 0);
    let [type, base] = pool[0];
    for (const [k, e] of pool) { roll -= e.weight; if (roll <= 0) { type = k; base = e; break; } }
    const [x, y] = this.randPos(8);
    const EL = effLevel(L);
    const hp = Math.max(1, Math.ceil(base.hp * (1 + (EL - 1) * 0.12) * this.d.enemyHp));
    this.enemies.push({ type, x, y, r: base.r, hp, speed: base.speed * (1 + (EL - 1) * 0.07) * this.d.enemySpeed, pts: base.pts, warn: 0.8, hitT: 0, kb: { x: 0, y: 0, t: 0 }, fireT: rnd(1.5, 3), seed: Math.random() * 10 });
  }

  spawnToast(x, y) {
    if (x === undefined) [x, y] = this.randPos(3);
    this.toasts.push({ x, y, seed: Math.random() * 6 });
  }

  burst(x, y, color, n = 10, sp = 120) {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, s = rnd(sp * 0.3, sp);
      this.parts.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: rnd(0.3, 0.7), color, size: ri(2, 4) });
    }
  }

  text(x, y, txt, color = "#FEF08A") { this.texts.push({ x, y, txt, color, life: 0.9 }); }

  // ---------- combat ----------
  tryAttack(ang) {
    const p = this.p;
    if (p.atk > 0) return;
    const w = WEAPONS[this.run.weapon];
    p.atk = w.cd; p.swing = 0.16; p.swingAng = ang; p.face = ang;
    sfx("swing");
    const targets = this.boss && !this.bossDead ? [...this.enemies, this.boss] : this.enemies;
    targets.forEach((e) => {
      if (e.warn > 0) return;
      const dx = e.x - p.x, dy = e.y - p.y, dist = Math.hypot(dx, dy);
      if (dist < w.range + e.r + 8 && (dist < e.r + p.r + 4 || angDiff(Math.atan2(dy, dx), ang) < w.arc / 2)) this.damageEnemy(e, w.dmg, Math.atan2(dy, dx));
    });
    this.hazards.forEach((h) => {
      if (h.type !== "roll" || h.t < h.warn || Math.hypot(h.x - p.x, h.y - p.y) > w.range + h.r) return;
      h.dead = true; this.popHazard(h); this.addScore(20); this.text(h.x, h.y - 16, "SMASH!", "#FDE047");
      if (++this.run.smashes >= 10) this.achieve("smash_10");
    });
  }

  damageEnemy(e, dmg, ang) {
    e.hp -= dmg; e.hitT = 0.12;
    this.text(e.x, e.y - e.r - 4, `-${dmg}`, "#FCA5A5");
    if (!e.isBoss && e.type !== "turret") e.kb = { x: Math.cos(ang) * 240, y: Math.sin(ang) * 240, t: 0.13 };
    this.burst(e.x, e.y, "#FDE68A", 5, 90);
    fx.spark(this.fx, e.x + rnd(-6, 6), e.y + rnd(-6, 6));
    sfx("hit");
    if (e.hp > 0) return;
    if (e.isBoss) {
      this.bossDead = true;
      this.burst(e.x, e.y, "#F97316", 60, 260);
      this.burst(e.x, e.y, "#FDE047", 40, 200);
      this.addScore(1500 * Math.ceil(this.run.level / 3));
      this.achieve("boss_slayer");
      if (!this.bossHurt) this.achieve("boss_flawless");
      this.cb.onDefeat?.(bossId(e.name));
      if (this.run.level === MAIN_LEVELS) this.achieve("overlord");
      for (let i = 0; i < 6; i++) this.spawnToast(e.x + rnd(-50, 50), e.y + rnd(-30, 30));
      this.text(e.x, e.y - 50, `${e.name.toUpperCase()} DEFEATED!`, "#FDE047");
      sfx("victory");
      return;
    }
    this.enemies = this.enemies.filter((x) => x !== e);
    this.run.kills++;
    this.cb.onDefeat?.(e.type);
    this.addScore(e.pts);
    this.burst(e.x, e.y, e.type === "mold" ? "#10B981" : e.type === "jam" ? "#8B5CF6" : "#B45309", 14);
    sfx("kill");
    fx.ring(this.fx, e.x, e.y, "#FFF3B0", e.r * 3);
    this.achieve("first_blood");
    if (this.run.kills >= 50) this.achieve("crumb_crusher");
    if (this.run.kills >= 100) this.achieve("crumb_apocalypse");
    if (Math.random() < this.d.heartDrop) this.pickups.push({ type: "heart", x: e.x, y: e.y });
    else if (Math.random() < (this.diffKey === "easy" ? 0.06 : 0.03)) this.spawnPower(e.x, e.y);
  }

  spawnPower(x, y) {
    if (x === undefined) [x, y] = this.randPos(4);
    this.pickups.push({ type: "power", kind: POWERS[ri(0, POWERS.length - 1)], x, y });
  }

  applyPower(kind) {
    if (kind === "shield") this.pw.shield = true;
    else this.pw[kind] = POWER_INFO[kind].dur;
    this.text(this.p.x, this.p.y - 24, POWER_INFO[kind].label + "!", POWER_INFO[kind].color);
    if (POWERS.filter((k) => (k === "shield" ? this.pw.shield : this.pw[k] > 0)).length >= 3) this.achieve("power_stack");
    sfx("weapon");
  }

  hurtPlayer(sx, sy) {
    const p = this.p;
    if (p.inv > 0 || this.phase !== "play" || this.pw.star > 0) return;
    if (this.pw.shield) {
      this.pw.shield = false; p.inv = 1;
      this.text(p.x, p.y - 24, "SHIELD BLOCK!", "#67E8F9");
      this.burst(p.x, p.y, "#67E8F9", 16);
      sfx("hit");
      return;
    }
    p.hp -= this.d.hitDmg;
    if (this.boss && !this.bossDead) this.bossHurt = true;
    p.inv = this.d.iframes;
    this.levelDamage = true;
    const a = Math.atan2(p.y - sy, p.x - sx);
    p.kb = { x: Math.cos(a) * 260, y: Math.sin(a) * 260, t: 0.15 };
    this.shake = 0.3;
    this.burst(p.x, p.y, "#FDE047", 12);
    fx.spark(this.fx, p.x, p.y, "#FCA5A5");
    sfx("hurt");
    if (p.hp > 0) return;
    this.run.lives--;
    this.run.livesLost++;
    this.burst(p.x, p.y, "#FDE047", 40, 200);
    sfx("die");
    if (this.run.lives <= 0) {
      this.phase = "gameover"; this.phaseT = 2.2;
      this.achieve("burnt_toast");
    } else {
      this.phase = "dying"; this.phaseT = 2;
    }
  }

  timeUp() {
    this.timedOut = true;
    this.p.hp = 0;
    this.run.lives--; this.run.livesLost++;
    sfx("die");
    if (this.run.lives <= 0) { this.phase = "gameover"; this.phaseT = 2.2; this.achieve("burnt_toast"); }
    else { this.phase = "dying"; this.phaseT = 2; }
  }

  levelComplete() {
    const L = this.run.level;
    this.addScore(500 * L);
    if (!this.levelDamage) this.achieve("untouchable");
    if (this.levelTime < 30) this.achieve("speedy");
    if (this.timeLimit - this.levelTime < 10) this.achieve("clutch");
    if (this.hazardsSpawned >= 3 && !this.hazardHit) this.achieve("hazard_dodger");
    if (this.run.lives === 1) this.achieve("last_life");
    sfx("clear");
    if (L === MAIN_LEVELS) {
      this.achieve(`beat_${this.diffKey}`);
      if (this.run.livesLost === 0) this.achieve("flawless");
      this.phase = "victory"; this.phaseT = 2.5;
    } else {
      this.phase = "exiting"; this.phaseT = 1.6;
    }
  }

  // ---------- update ----------
  update(dt) {
    this.t += dt;
    this.shake = Math.max(0, this.shake - dt);
    this.parts.forEach((q) => { q.x += q.vx * dt; q.y += q.vy * dt; q.vx *= 0.9; q.vy *= 0.9; q.life -= dt; });
    this.parts = this.parts.filter((q) => q.life > 0);
    this.texts.forEach((q) => { q.y -= 30 * dt; q.life -= dt; });
    this.texts = this.texts.filter((q) => q.life > 0);
    this.fx = updateFx(this.fx, dt);
    if (this.phase !== "play") {
      this.phaseT -= dt;
      if (this.phaseT > 0) return;
      if (this.phase === "intro") this.phase = "play";
      else if (this.phase === "dying") this.startLevel();
      else if (this.phase === "exiting") { this.run.level++; this.startLevel(); }
      else if (this.phase === "gameover") { this.phase = "done"; this.cb.onGameOver?.(this.stats()); }
      else if (this.phase === "victory") { this.phase = "done"; this.cb.onVictory?.(this.stats()); }
      return;
    }
    this.levelTime += dt;
    if (this.levelTime >= this.timeLimit) { this.timeUp(); return; }
    this.updatePlayer(dt);
    this.updateEnemies(dt);
    if (this.boss && !this.bossDead) this.updateBoss(dt);
    this.updateBullets(dt);
    if (this.hazard || this.hazards.length) this.updateHazards(dt);
    this.updateItems(dt);
  }

  updatePlayer(dt) {
    const p = this.p, i = this.input;
    let ix = (i.right ? 1 : 0) - (i.left ? 1 : 0), iy = (i.down ? 1 : 0) - (i.up ? 1 : 0);
    const len = Math.hypot(ix, iy);
    if (len) { ix /= len; iy /= len; p.face = Math.atan2(iy, ix); p.walk += dt; }
    const spd = this.pw.speed > 0 ? 235 : 155;
    let vx = ix * spd, vy = iy * spd;
    if (p.kb.t > 0) { vx += p.kb.x; vy += p.kb.y; p.kb.t -= dt; }
    this.move(p, vx * dt, vy * dt);
    p.atk -= dt; p.swing -= dt; p.inv -= dt;
    ["speed", "double", "magnet", "star"].forEach((k) => { this.pw[k] = Math.max(0, this.pw[k] - dt); });
    this.powerT -= dt;
    if (this.powerT <= 0) { this.spawnPower(); this.powerT = this.diffKey === "easy" ? 30 : 45; }
    if (i.atk) this.tryAttack(p.face);
  }

  updateEnemies(dt) {
    const p = this.p;
    this.flowT = (this.flowT ?? 0) - dt;
    if (this.flowT <= 0) { this.computeFlow(); this.flowT = 0.25; }
    const L = this.run.level;
    for (const e of this.enemies) {
      if (e.warn > 0) { e.warn -= dt; continue; }
      e.hitT -= dt;
      if (e.kb.t > 0) { this.move(e, e.kb.x * dt, e.kb.y * dt); e.kb.t -= dt; }
      else if (e.type === "turret") {
        e.fireT -= dt;
        if (e.fireT <= 0) {
          const a = Math.atan2(p.y - e.y, p.x - e.x), s = (150 + L * 10) * Math.min(1.2, this.d.enemySpeed);
          this.bullets.push({ x: e.x, y: e.y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, r: 5 });
          e.fireT = rnd(2, 3) / this.d.bullets / (1 + L * 0.04);
          sfx("shoot");
        }
      } else if (e.type === "ghost") {
        const a = Math.atan2(p.y - e.y, p.x - e.x);
        e.x += Math.cos(a) * e.speed * dt; e.y += Math.sin(a) * e.speed * dt;
      } else {
        let tx = p.x, ty = p.y;
        const cx = Math.floor(e.x / TILE), cy = Math.floor(e.y / TILE);
        const cd = this.flow[cy]?.[cx] ?? -1;
        if (cd > 1) {
          let best = cd, bx = cx, by = cy;
          for (const [dx, dy] of DIRS) {
            const v = this.flow[cy + dy]?.[cx + dx] ?? -1;
            if (v >= 0 && v < best) { best = v; bx = cx + dx; by = cy + dy; }
          }
          tx = bx * TILE + 16; ty = by * TILE + 16;
        }
        const a = Math.atan2(ty - e.y, tx - e.x);
        const sp = e.type === "jam" ? e.speed * (0.35 + Math.abs(Math.sin(this.t * 5 + e.seed))) : e.speed;
        this.move(e, Math.cos(a) * sp * dt, Math.sin(a) * sp * dt);
      }
      if (Math.hypot(e.x - p.x, e.y - p.y) < e.r + p.r - 2) {
        if (this.pw.star > 0) this.damageEnemy(e, 99, Math.atan2(e.y - p.y, e.x - p.x));
        else this.hurtPlayer(e.x, e.y);
      }
    }
    for (let a = 0; a < this.enemies.length; a++) for (let b = a + 1; b < this.enemies.length; b++) {
      const e1 = this.enemies[a], e2 = this.enemies[b];
      const dx = e2.x - e1.x, dy = e2.y - e1.y, d = Math.hypot(dx, dy) || 1, min = e1.r + e2.r;
      if (d < min) {
        const push = (min - d) / 2;
        if (e1.type !== "turret") this.move(e1, (-dx / d) * push, (-dy / d) * push);
        if (e2.type !== "turret") this.move(e2, (dx / d) * push, (dy / d) * push);
      }
    }
    this.spawnT -= dt;
    if (this.spawnT <= 0) { if (this.enemies.length < this.maxEnemies) this.spawnEnemy(); this.spawnT = this.spawnInt; }
  }

  startBossMove(b) {
    const kind = b.kit.moves[b.moveIdx++ % b.kit.moves.length];
    b.move = { kind, t: 0, stage: 0 };
    this.text(b.x, b.y - b.r - 22, moveLabel(kind, b.kit), "#FDE047");
    const p = this.p, cl = (v, max) => Math.max(TILE * 1.5, Math.min(max - TILE * 1.5, v)), easy = this.diffKey === "easy";
    if (kind === "rain") {
      const n = Math.round((b.enraged ? 7 : 5) * (easy ? 0.6 : 1));
      for (let i = 0; i < n; i++) this.hazards.push({ type: "fall", look: b.kit.rain, r: 10, t: 0, rot: 0, warn: 0.9 + i * 0.2, x: cl(p.x + (i ? rnd(-130, 130) : 0), W), y: cl(p.y + (i ? rnd(-110, 110) : 0), H) });
    } else if (kind === "pillars") {
      const n = b.enraged ? 4 : 3;
      for (let i = 0; i < n; i++) { const a = (i / n) * Math.PI * 2; this.hazards.push({ type: "burst", look: b.kit.pillar, r: 24, t: 0, warn: 0.9 + i * 0.25, x: cl(p.x + (i ? Math.cos(a) * 70 : 0), W), y: cl(p.y + (i ? Math.sin(a) * 70 : 0), H) }); }
    }
  }

  runBossMove(b, dt, sp, a) {
    const m = b.move, p = this.p;
    m.t += dt;
    const end = () => { b.move = null; b.moveT = moveCooldown(b, this.diffKey); };
    const next = (st) => { m.stage = st; m.t = 0; };
    const go = (ang, v) => { b.x = Math.min(W - 60, Math.max(60, b.x + Math.cos(ang) * v * dt)); b.y = Math.min(H - 60, Math.max(60, b.y + Math.sin(ang) * v * dt)); };
    if (m.kind === "dash") {
      if (m.stage === 0) { m.ang = a; b.face = a; if (m.t > 0.6) next(1); }
      else { go(m.ang, sp * 3.6); if (m.t > 0.65) end(); }
    } else if (m.kind === "slam") {
      if (m.stage === 0 && m.t > 0.6) {
        this.bullets.push({ ring: true, x: b.x, y: b.y, rad: b.r, color: b.kit.wave, r: 0 });
        this.burst(b.x, b.y, b.kit.wave, 24, 200); sfx("boss"); end();
      }
    } else if (m.kind === "swing") {
      const reach = 60 + b.r * 0.6;
      if (m.stage === 0) { go(a, sp * 1.8); b.face = a; if (Math.hypot(p.x - b.x, p.y - b.y) < reach || m.t > 1.6) next(1); return; }
      if (m.stage === 1 && m.t > 0.45) { next(2); sfx("swing"); }
      else if (m.stage === 2) {
        if (!m.hit && Math.hypot(p.x - b.x, p.y - b.y) < reach + p.r + 16 && angDiff(a, b.face) < 1.3) { m.hit = true; this.hurtPlayer(b.x, b.y); }
        if (m.t > 0.25) next(3);
      } else if (m.stage === 3 && m.t > 0.3) end();
    } else if (m.t > 1.3) end();
  }

  updateBoss(dt) {
    const b = this.boss, p = this.p;
    b.hitT -= dt;
    b.enraged = b.hp < b.maxHp / 2;
    const a = Math.atan2(p.y - b.y, p.x - b.x);
    const sp = b.speed * (b.enraged ? 1.5 : 1) * b.pw.speed;
    if (!b.move || b.move.kind !== "dash") b.face = a;
    if (b.move) this.runBossMove(b, dt, sp, a);
    else {
      b.moveT -= dt;
      if (b.moveT <= 0) this.startBossMove(b);
      b.x = Math.min(W - 60, Math.max(60, b.x + Math.cos(a) * sp * dt));
      b.y = Math.min(H - 60, Math.max(60, b.y + Math.sin(a) * sp * dt));
      b.ringT -= dt; b.aimT -= dt;
      if (b.ringT <= 0) {
        const n = Math.max(4, Math.round((b.enraged ? 16 : 11) * this.d.bullets * b.ringK));
        const off = Math.random() * Math.PI;
        for (let i = 0; i < n; i++) {
          const ang = off + (i / n) * Math.PI * 2;
          this.bullets.push({ x: b.x, y: b.y, vx: Math.cos(ang) * 140, vy: Math.sin(ang) * 140, r: 6 });
        }
        b.ringT = (b.enraged ? 1.3 : 2) / Math.sqrt(this.d.bullets) / b.pw.atk;
        sfx("shoot");
      }
      if (b.aimT <= 0) {
        const shots = b.enraged ? 3 : 1;
        for (let i = 0; i < shots; i++) {
          const ang = a + (i - (shots - 1) / 2) * 0.22;
          this.bullets.push({ x: b.x, y: b.y, vx: Math.cos(ang) * 230, vy: Math.sin(ang) * 230, r: 5 });
        }
        b.aimT = (b.enraged ? 0.8 : 1.4) / this.d.bullets / b.pw.atk;
      }
    }
    if (Math.hypot(b.x - p.x, b.y - p.y) < b.r + p.r) this.hurtPlayer(b.x, b.y);
  }

  drawBossWeapon(b, t) {
    const ctx = this.ctx, w = WEAPONS[b.kit.weapon], m = b.move;
    const a = weaponAngle(b, b.face, 1), hx = b.x + Math.cos(b.face) * b.r * 0.7, hy = b.y + Math.sin(b.face) * b.r * 0.7;
    if (m?.kind === "swing" && m.stage === 2) {
      ctx.fillStyle = "rgba(255,255,255,0.25)"; ctx.beginPath(); ctx.moveTo(hx, hy);
      ctx.arc(hx, hy, 60 + b.r * 0.6, Math.min(b.face - 2.2, a), Math.max(b.face - 2.2, a)); ctx.closePath(); ctx.fill();
    }
    ctx.save(); ctx.translate(hx, hy); ctx.scale(1.8, 1.8);
    drawWeapon(ctx, 0, 0, a, w, Math.max(14, w.range - 22));
    ctx.restore();
    if (isWindup(b) && Math.sin(t * 24) > -0.3) { ctx.font = "20px Silkscreen, monospace"; ctx.textAlign = "center"; ctx.fillStyle = "#000"; ctx.fillText("!", b.x + 2, b.y - b.r - 6); ctx.fillStyle = "#EF4444"; ctx.fillText("!", b.x, b.y - b.r - 8); }
  }

  spawnHazard() {
    const hz = pickHazard(this.hazard), p = this.p;
    const h = { type: hz.type, look: hz.look, r: hz.type === "burst" ? 24 : hz.r, t: 0, rot: 0, vx: 0, vy: 0 };
    if (hz.type === "roll") {
      [h.x, h.y] = this.randPos(9);
      const a = Math.atan2(p.y - h.y, p.x - h.x), s = rnd(150, 190) * Math.sqrt(this.d.enemySpeed);
      h.vx = Math.cos(a) * s; h.vy = Math.sin(a) * s; h.warn = 0.7;
    } else {
      h.x = Math.max(TILE * 1.5, Math.min(W - TILE * 1.5, p.x + rnd(-50, 50)));
      h.y = Math.max(TILE * 1.5, Math.min(H - TILE * 1.5, p.y + rnd(-50, 50)));
      h.warn = hz.type === "fall" ? 1.0 : 1.1;
    }
    this.hazards.push(h);
    this.hazardsSpawned++;
  }

  hazardHurt(x, y) { this.hazardHit = true; this.hurtPlayer(x, y); }

  popHazard(h) { this.burst(h.x, h.y, hazardColor(h.look), 12, 140); fx.ring(this.fx, h.x, h.y, hazardColor(h.look), 30); return false; }

  updateHazards(dt) {
    const p = this.p;
    this.hazardT -= dt;
    if (this.hazard && this.hazardT <= 0) { this.spawnHazard(); this.hazardT = hazardInterval(this.run.level, this.d) * (this.boss && !this.bossDead ? 1.6 : 1); }
    this.hazards = this.hazards.filter((h) => {
      h.t += dt;
      if (h.dead) return false;
      if (h.t < h.warn) return true;
      const dist = Math.hypot(h.x - p.x, h.y - p.y);
      if (h.type === "burst") {
        if (h.t - h.warn > 0.8) return false;
        if (dist < h.r + p.r - 4) this.hazardHurt(h.x, h.y);
        return true;
      }
      if (h.type === "fall") { if (dist < h.r + p.r + 6) this.hazardHurt(h.x, h.y); return this.popHazard(h); }
      h.x += h.vx * dt; h.y += h.vy * dt; h.rot += (Math.hypot(h.vx, h.vy) * dt) / h.r;
      if (this.solid(Math.floor(h.x / TILE), Math.floor(h.y / TILE))) return this.popHazard(h);
      if (dist < h.r + p.r - 2) { this.hazardHurt(h.x, h.y); return this.popHazard(h); }
      return true;
    });
  }

  updateBullets(dt) {
    const p = this.p;
    this.bullets = this.bullets.filter((q) => {
      if (q.ring) {
        q.rad += 230 * dt;
        if (!q.hit && Math.abs(Math.hypot(p.x - q.x, p.y - q.y) - q.rad) < p.r + 6) { q.hit = true; this.hurtPlayer(q.x, q.y); }
        return q.rad < 360;
      }
      q.x += q.vx * dt; q.y += q.vy * dt;
      if (this.solid(Math.floor(q.x / TILE), Math.floor(q.y / TILE))) { this.burst(q.x, q.y, "#F97316", 4, 60); return false; }
      if (Math.hypot(q.x - p.x, q.y - p.y) < q.r + p.r - 2) { this.hurtPlayer(q.x, q.y); return false; }
      return true;
    });
  }

  updateItems(dt) {
    const p = this.p;
    this.toasts = this.toasts.filter((q) => {
      const dist = Math.hypot(q.x - p.x, q.y - p.y);
      if (this.pw.magnet > 0 && dist < 220) { q.x += ((p.x - q.x) / dist) * 320 * dt; q.y += ((p.y - q.y) / dist) * 320 * dt; }
      if (dist > p.r + 12) return true;
      const n = this.pw.double > 0 ? 2 : 1;
      this.levelToast += n; this.run.toastTotal += n;
      this.addScore(100 * n);
      this.text(q.x, q.y - 14, `+${n} TOAST`);
      this.burst(q.x, q.y, "#FDE68A", 10);
      fx.ring(this.fx, q.x, q.y, "#FFD43B", 26); fx.glint(this.fx, q.x, q.y);
      sfx("toast");
      this.achieve("first_toast");
      if (this.run.toastTotal >= 100) this.achieve("toast_hoarder");
      return false;
    });
    const maxToast = this.levelToast >= this.goal ? 1 : 4;
    if (this.toasts.length < maxToast) {
      this.toastT -= dt;
      if (this.toastT <= 0) { this.spawnToast(); this.toastT = 1.2; }
    }
    this.pickups = this.pickups.filter((q) => {
      if (Math.hypot(q.x - p.x, q.y - p.y) > p.r + 14) return true;
      if (q.type === "power") {
        this.applyPower(q.kind);
      } else if (q.type === "heart") {
        if (p.hp >= this.maxHp) return true;
        p.hp = Math.min(this.maxHp, p.hp + 2);
        this.text(q.x, q.y - 14, "+HEART", "#FCA5A5");
        sfx("heart");
      } else {
        this.run.weapon = q.weapon;
        this.text(q.x, q.y - 20, WEAPONS[q.weapon].name.toUpperCase() + "!", "#FDE047");
        this.burst(q.x, q.y, "#FDE047", 30, 180);
        sfx("weapon");
        this.achieve({ dinner_fork: "fork_found", baguette_sword: "baguette", golden_spatula: "spatula" }[q.weapon]);
      }
      return false;
    });
    if (!this.exit && this.levelToast >= this.goal && this.bossDead) {
      const [x, y] = this.randPos(6);
      this.exit = { x, y };
      this.text(p.x, p.y - 30, "EXIT OPEN!", "#FDE047");
      sfx("level");
    }
    if (this.exit && Math.hypot(this.exit.x - p.x, this.exit.y - p.y) < p.r + 16) this.levelComplete();
  }

  // ---------- draw ----------
  draw() {
    const ctx = this.ctx, t = this.t, p = this.p;
    ctx.imageSmoothingEnabled = false;
    ctx.save();
    ctx.drawImage(this.bg, 0, 0);
    if (this.exit) this.drawExit(this.exit, t);
    this.toasts.forEach((q) => drawSprite(ctx, SPR.toast, q.x, q.y + Math.sin(t * 4 + q.seed) * 2, 3));
    this.pickups.forEach((q) => {
      const bob = Math.sin(t * 4) * 3;
      if (q.type === "heart") drawSprite(ctx, SPR.heart, q.x, q.y + bob, 3);
      else if (q.type === "power") {
        const info = POWER_INFO[q.kind];
        ctx.fillStyle = "rgba(0,0,0,0.35)"; ctx.fillRect(q.x - 12, q.y + 13, 24, 4);
        ctx.fillStyle = info.color; ctx.fillRect(q.x - 12, q.y - 12 + bob, 24, 24);
        ctx.fillStyle = "#0F172A"; ctx.fillRect(q.x - 9, q.y - 9 + bob, 18, 18);
        ctx.font = "11px Silkscreen, monospace"; ctx.textAlign = "center";
        ctx.fillStyle = info.color; ctx.fillText(info.icon, q.x, q.y + 4 + bob);
      } else {
        ctx.fillStyle = `rgba(253,224,71,${0.25 + Math.sin(t * 6) * 0.15})`;
        ctx.beginPath(); ctx.arc(q.x, q.y, 22, 0, Math.PI * 2); ctx.fill();
        drawWeapon(ctx, q.x - 26, q.y + bob, -0.6, WEAPONS[q.weapon], 22);
      }
    });
    const toP = (e) => Math.atan2(p.y - e.y, p.x - e.x);
    this.enemies.forEach((e) => {
      if (e.warn > 0) {
        ctx.strokeStyle = `rgba(239,68,68,${0.4 + Math.sin(t * 20) * 0.3})`;
        ctx.lineWidth = 2;
        ctx.strokeRect(e.x - e.r, e.y - e.r, e.r * 2, e.r * 2);
      } else drawEnemy(ctx, e, t, toP(e));
    });
    this.hazards.forEach((h) => drawHazard(ctx, h, t, true));
    const bb = this.boss;
    if (bb && !this.bossDead) {
      if (!bb.type) drawBoss(ctx, bb, t, toP(bb));
      else { ctx.save(); ctx.translate(bb.x, bb.y); ctx.scale(bb.scale, bb.scale); drawBossExtras(ctx, bb.name, ENEMIES[bb.type].r, t, "back"); drawEnemy(ctx, { ...bb, x: 0, y: 0, r: ENEMIES[bb.type].r }, t, toP(bb)); drawBossExtras(ctx, bb.name, ENEMIES[bb.type].r, t, "front"); ctx.restore(); }
      this.drawBossWeapon(bb, t);
    }
    if (!(this.phase === "gameover" || this.phase === "dying" || this.phase === "done") || this.phaseT > 1.6) {
      if (this.pw.star > 0) { ctx.fillStyle = "rgba(253,224,71,0.4)"; ctx.beginPath(); ctx.arc(p.x, p.y, 22, 0, Math.PI * 2); ctx.fill(); }
      if (this.pw.shield) { ctx.strokeStyle = "#67E8F9"; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(p.x, p.y, 20 + Math.sin(t * 6), 0, Math.PI * 2); ctx.stroke(); }
      ctx.globalAlpha = p.inv > 0 ? 0.6 : 1;
      drawHero(ctx, p, t, this.phase === "exiting" || this.phase === "victory", this.skin);
      ctx.globalAlpha = 1;
      this.drawPlayerWeapon();
    }
    this.bullets.forEach((q) => {
      if (q.ring) { ctx.strokeStyle = q.color; ctx.globalAlpha = Math.max(0.2, 1 - q.rad / 360); ctx.lineWidth = 8; ctx.beginPath(); ctx.arc(q.x, q.y, q.rad, 0, Math.PI * 2); ctx.stroke(); ctx.globalAlpha = 1; return; }
      ctx.fillStyle = "#F97316"; ctx.fillRect(q.x - q.r, q.y - q.r, q.r * 2, q.r * 2);
      ctx.fillStyle = "#FEF08A"; ctx.fillRect(q.x - 2, q.y - 2, 4, 4);
    });
    this.parts.forEach((q) => { ctx.globalAlpha = Math.min(1, q.life * 2); ctx.fillStyle = q.color; ctx.fillRect(q.x, q.y, q.size, q.size); });
    drawFx(ctx, this.fx);
    ctx.globalAlpha = 1;
    ctx.font = "12px Silkscreen, monospace";
    ctx.textAlign = "center";
    this.texts.forEach((q) => {
      ctx.globalAlpha = Math.min(1, q.life * 2);
      ctx.fillStyle = "#000"; ctx.fillText(q.txt, q.x + 1, q.y + 1);
      ctx.fillStyle = q.color; ctx.fillText(q.txt, q.x, q.y);
    });
    ctx.globalAlpha = 1;
    ctx.restore();
    drawVignette(ctx, W, H);
    drawFade(ctx, W, H, this.phase === "intro" ? (this.phaseT - 1.9) / 0.5 : this.phase === "exiting" ? (0.5 - this.phaseT) / 0.5 : 0);
    this.drawBanner();
  }

  drawExit(ex, t) {
    const ctx = this.ctx, pulse = Math.sin(t * 5) * 4;
    ctx.fillStyle = "rgba(253,224,71,0.25)";
    ctx.beginPath(); ctx.arc(ex.x, ex.y, 26 + pulse, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#F59E0B"; ctx.fillRect(ex.x - 18, ex.y - 6, 36, 14);
    ctx.fillStyle = "#FEF08A"; ctx.fillRect(ex.x - 14, ex.y - 12, 28, 8);
    ctx.fillStyle = "#FFF"; ctx.fillRect(ex.x - 10, ex.y - 11, 6, 3);
    ctx.font = "10px Silkscreen, monospace"; ctx.textAlign = "center";
    ctx.fillStyle = "#FEF08A"; ctx.fillText("EXIT", ex.x, ex.y + 24);
  }

  drawPlayerWeapon() {
    const p = this.p, w = WEAPONS[this.run.weapon], ctx = this.ctx;
    const len = Math.max(14, w.range - 22);
    if (p.swing > 0) {
      const k = 1 - p.swing / 0.16;
      const ang = p.swingAng - w.arc / 2 + w.arc * k;
      ctx.fillStyle = "rgba(254,240,138,0.18)";
      ctx.beginPath(); ctx.moveTo(p.x, p.y);
      ctx.arc(p.x, p.y, w.range + 6, p.swingAng - w.arc / 2, ang); ctx.closePath(); ctx.fill();
      drawWeapon(ctx, p.x, p.y, ang, w, len);
    } else drawWeapon(ctx, p.x, p.y, p.face + 0.9, w, len * 0.6);
  }

  drawBanner() {
    const ctx = this.ctx;
    const map = {
      intro: [`LEVEL ${this.run.level}`, `${this.theme.name.toUpperCase()} — COLLECT ${this.goal} TOAST${this.boss ? ` & BEAT THE ${this.boss.name.toUpperCase()}` : ""}`],
      dying: [this.timedOut ? "TIME UP!" : "OUCH! LIFE LOST", `${this.run.lives} ${this.run.lives === 1 ? "LIFE" : "LIVES"} LEFT — RESTARTING LEVEL`],
      gameover: ["BURNT TOAST", "ALL LIVES LOST"],
      exiting: ["LEVEL CLEAR!", "BUTTER BOY MARCHES ON..."],
      victory: ["YOU DID IT!", "THE TOAST IS YOURS"],
    };
    const b = map[this.phase];
    if (!b) return;
    ctx.fillStyle = "rgba(15,23,42,0.78)";
    ctx.fillRect(0, H / 2 - 70, W, 140);
    ctx.fillStyle = "#FBBF24"; ctx.fillRect(0, H / 2 - 70, W, 4); ctx.fillRect(0, H / 2 + 66, W, 4);
    ctx.textAlign = "center";
    ctx.font = "48px Silkscreen, monospace";
    ctx.fillStyle = "#713F12"; ctx.fillText(b[0], W / 2 + 4, H / 2 + 4);
    ctx.fillStyle = this.phase === "gameover" || this.phase === "dying" ? "#EF4444" : "#FDE047";
    ctx.fillText(b[0], W / 2, H / 2);
    ctx.font = "16px Silkscreen, monospace";
    ctx.fillStyle = "#F8FAFC"; ctx.fillText(b[1], W / 2, H / 2 + 40);
    if (this.phase === "intro" && this.hazard) { ctx.font = "12px Silkscreen, monospace"; ctx.fillStyle = "#FCA5A5"; ctx.fillText(`WATCH OUT: ${this.hazard.label}!`, W / 2, H / 2 + 60); }
  }
}
